import { Component, Input } from '@angular/core';
import {
    marketingBlogEntity,
    marketingNewsletterEntity,
    marketingSocialPostEntity,
} from 'mj_generatedentities';
import { ProgramType, PlatformKey } from '../../shared/icon-pair.component';

interface ChannelTile {
    path: string;
    label: string;
    iconProgram: ProgramType;
    iconPlatform: PlatformKey;
    phase: 'Phase 1' | 'Phase 2' | 'Phase 3';
    count: number | string;
    countLabel: string;
    sub: string;
}

/**
 * Programs overview — channel tile grid (mockup pattern).
 * Each tile shows a program's icon-pair, phase pill, item count, sub-text.
 */
@Component({
    standalone: false,
    selector: 'mkt-ws-programs-overview',
    template: `
<mkt-ws-page-head
    [eyebrow]="'Programs' + (companyName ? ' · ' + companyName : '')"
    title="What's shipping this week."
    sub="Standing operations across active channels. Winners graduated from Experiments land here as evergreen programs.">
</mkt-ws-page-head>

<!-- Channel tile grid -->
<div class="po__tiles">
    <button *ngFor="let t of tiles" type="button" class="po__tile" (click)="navigate(t.path)">
        <div class="po__tile-head">
            <div class="po__tile-id">
                <mkt-ws-icon-pair [program]="t.iconProgram" [platform]="t.iconPlatform"></mkt-ws-icon-pair>
                <span class="po__caps">{{ t.label }}</span>
            </div>
            <mkt-ws-pill [variant]="t.phase === 'Phase 1' ? 'good' : 'muted'">{{ t.phase }}</mkt-ws-pill>
        </div>
        <div class="po__tile-count">
            <span class="po__count">{{ t.count }}</span>
            <span class="po__count-label">{{ t.countLabel }}</span>
        </div>
        <div class="po__tile-sub">{{ t.sub }}</div>
    </button>

    <div class="po__tile po__tile--later">
        <div class="po__caps" style="margin-bottom: 8px;">Later phases</div>
        <div class="po__tile-sub">Events beyond webinars · Customer marketing · added when Phase 2 is solid.</div>
    </div>
</div>

<!-- Cross-channel queue (real, derived from blogs + social + newsletters) -->
<mkt-ws-card [hasHead]="true" [hasFoot]="true" [noPadding]="true" style="margin-top: 24px;">
    <div head style="display:flex; align-items:center; gap:8px;">
        <span class="po__caps-ink">Cross-channel queue · this week</span>
        <mkt-ws-pill variant="muted">{{ totalQueue }} items</mkt-ws-pill>
    </div>
    <table class="po__queue">
        <thead>
            <tr>
                <th class="po__col-channel">Channel</th>
                <th>Item</th>
                <th class="po__col-status">Status</th>
            </tr>
        </thead>
        <tbody>
            <tr *ngFor="let item of queue" (click)="navigate(item.path)" style="cursor: pointer;">
                <td><mkt-ws-icon-pair [program]="item.iconProgram" [platform]="item.iconPlatform"></mkt-ws-icon-pair></td>
                <td class="po__queue-asset">
                    <span class="po__queue-link">{{ item.title }}</span>
                    <div *ngIf="item.sub" class="po__queue-sub">{{ item.sub }}</div>
                </td>
                <td class="po__col-status"><mkt-ws-pill [variant]="item.statusVariant">{{ item.status }}</mkt-ws-pill></td>
            </tr>
            <tr *ngIf="queue.length === 0">
                <td colspan="3" style="padding: 32px; text-align: center; color: var(--mj-text-secondary, #71717a); font-size: 12px;">
                    Queue is empty. Trigger a marketing job from
                    <button type="button" class="po__link" (click)="navigate('foundation/activity-log')">Foundation → Activity log</button>.
                </td>
            </tr>
        </tbody>
    </table>
    <div foot>
        <span>Drafts, scheduled, and recently published items across all channels.</span>
        <button type="button" class="po__link" (click)="navigate('foundation/approvals')">Open approvals <i class="fa-solid fa-arrow-right"></i></button>
    </div>
</mkt-ws-card>
    `,
    styles: [`
        :host { display: block; }
        .po__caps { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); }
        .po__caps-ink { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-primary, #1a1a1a); }
        .po__link { color: var(--mj-brand-primary, #6366f1); text-decoration: none; font-weight: 500; display: inline-flex; align-items: center; gap: 4px; cursor: pointer; background: transparent; border: none; padding: 0; font-family: inherit; font-size: inherit; }
        .po__link:hover { text-decoration: underline; }

        .po__tiles { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
        .po__tile { display: block; background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 10px; padding: 18px 20px; text-decoration: none; color: inherit; transition: border-color 0.15s; cursor: pointer; text-align: left; font-family: inherit; font-size: inherit; width: 100%; }
        .po__tile:hover { border-color: var(--mj-brand-primary, #6366f1); }
        .po__tile--later { border-style: dashed; cursor: default; }
        .po__tile--later:hover { border-color: var(--mj-border-default, #e0e0e0); }
        .po__tile-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
        .po__tile-id { display: flex; align-items: center; gap: 8px; }
        .po__tile-count { display: flex; align-items: baseline; gap: 6px; margin-bottom: 6px; }
        .po__count { font-size: 24px; font-weight: 600; color: var(--mj-text-primary, #1a1a1a); font-variant-numeric: tabular-nums; line-height: 1.1; }
        .po__count-label { font-size: 12px; color: var(--mj-text-secondary, #71717a); }
        .po__tile-sub { font-size: 11.5px; color: var(--mj-text-secondary, #71717a); line-height: 1.5; }

        .po__queue { width: 100%; border-collapse: collapse; font-size: 13px; }
        .po__queue thead { background: var(--mj-bg-page, #f5f5f5); }
        .po__queue th { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); padding: 10px 16px; text-align: left; }
        .po__queue tbody tr { border-top: 1px solid var(--mj-border-default, #e0e0e0); transition: background 0.15s; }
        .po__queue tbody tr:hover { background: var(--mj-bg-page, #f5f5f5); }
        .po__queue td { padding: 12px 16px; vertical-align: middle; }
        .po__col-channel { width: 100px; }
        .po__col-status { width: 120px; text-align: right; }
        .po__queue td:last-child { text-align: right; }
        .po__queue-asset { max-width: 540px; }
        .po__queue-link { color: var(--mj-text-primary, #1a1a1a); text-decoration: none; font-weight: 500; }
        .po__queue-link:hover { color: var(--mj-brand-primary, #6366f1); }
        .po__queue-sub { font-size: 11.5px; color: var(--mj-text-secondary, #71717a); margin-top: 2px; }

        @media (max-width: 1100px) { .po__tiles { grid-template-columns: repeat(2, 1fr); } }
    `]
})
export class ProgramsOverviewSection {
    @Input() companyId = '';
    @Input() companyName = '';
    @Input() blogs: marketingBlogEntity[] = [];
    @Input() socialPosts: marketingSocialPostEntity[] = [];
    @Input() newsletters: marketingNewsletterEntity[] = [];
    @Input() navigate: (path: string) => void = () => {};

    get tiles(): ChannelTile[] {
        const draft = (arr: { Get: (k: string) => unknown }[]) => arr.filter(x => /draft|review/i.test(String(x.Get('Status') ?? ''))).length;
        return [
            { path: 'programs/blog', label: 'Blog', iconProgram: 'blog', iconPlatform: 'hubspot', phase: 'Phase 1',
              count: this.blogs.length, countLabel: 'in pipeline',
              sub: `${draft(this.blogs)} draft · podcast-to-blog flow` },
            { path: 'programs/social', label: 'Social', iconProgram: 'social', iconPlatform: 'linkedin', phase: 'Phase 1',
              count: this.socialPosts.length, countLabel: 'queued',
              sub: 'Company posts + employee opt-in reposts' },
            { path: 'programs/email', label: 'Email', iconProgram: 'email', iconPlatform: 'rasa', phase: 'Phase 2',
              count: this.newsletters.length, countLabel: 'sends',
              sub: 'Newsletters, weekly digests, nurture sequences' },
            { path: 'programs/video', label: 'Video', iconProgram: 'video', iconPlatform: 'youtube', phase: 'Phase 3',
              count: 0, countLabel: 'in pipeline', sub: 'Not yet active · preview mode' },
            { path: 'programs/sales-enablement', label: 'Sales Enablement', iconProgram: 'sales-enablement', iconPlatform: 'canva', phase: 'Phase 2',
              count: 0, countLabel: 'requests', sub: 'One-pagers, battlecards, vertical sheets' },
            { path: 'programs/paid', label: 'Paid', iconProgram: 'paid', iconPlatform: 'google', phase: 'Phase 3',
              count: '—', countLabel: 'committed', sub: 'Google, LinkedIn, Rasa.io paid placements' },
            { path: 'programs/lead-ops', label: 'Lead Ops', iconProgram: 'lead-ops', iconPlatform: 'hubspot', phase: 'Phase 2',
              count: 0, countLabel: 'handoffs', sub: 'AI SDR handoffs, scoring, context packs' },
        ];
    }

    get queue(): { iconProgram: ProgramType; iconPlatform: PlatformKey; title: string; sub?: string; status: string; statusVariant: 'good' | 'warn' | 'bad' | 'info' | 'muted'; path: string }[] {
        const out: { iconProgram: ProgramType; iconPlatform: PlatformKey; title: string; sub?: string; status: string; statusVariant: 'good' | 'warn' | 'bad' | 'info' | 'muted'; path: string }[] = [];
        for (const b of this.blogs.slice(0, 8)) {
            const status = String(b.Get('Status') ?? '');
            out.push({ iconProgram: 'blog', iconPlatform: 'hubspot', title: b.Title || 'Untitled', status, statusVariant: this.toVariant(status), path: 'programs/blog' });
        }
        for (const sp of this.socialPosts.slice(0, 5)) {
            const status = String(sp.Get('Status') ?? sp.Get('ContentStatusID') ?? '');
            const content = String(sp.Get('Content') ?? '').slice(0, 80);
            out.push({ iconProgram: 'social', iconPlatform: 'linkedin', title: content || 'Social post', status, statusVariant: this.toVariant(status), path: 'programs/social' });
        }
        for (const n of this.newsletters.slice(0, 3)) {
            const status = String(n.Get('Status') ?? '');
            const title = String(n.Get('Subject') ?? n.Get('Title') ?? '') || 'Newsletter';
            out.push({ iconProgram: 'email', iconPlatform: 'rasa', title, status, statusVariant: this.toVariant(status), path: 'programs/email' });
        }
        return out;
    }

    get totalQueue(): number {
        return this.queue.length;
    }

    private toVariant(status: string): 'good' | 'warn' | 'bad' | 'info' | 'muted' {
        const s = status.toLowerCase();
        if (s.includes('publish') || s.includes('approved') || s.includes('ready') || s.includes('scheduled')) return 'good';
        if (s.includes('review') || s.includes('draft')) return 'warn';
        if (s.includes('reject')) return 'bad';
        return 'muted';
    }
}
